"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const statsData = [{
  num: 99,
  suffix: ".9%",
  labelZh: "System Uptime",
  labelEn: "System Uptime"
}, {
  num: 500,
  suffix: "+",
  labelZh: "AI Models Deployed",
  labelEn: "AI Models Deployed"
}, {
  num: 24,
  suffix: "/7",
  labelZh: "Threat Monitoring",
  labelEn: "Threat Monitoring"
}];
function AnimatedStat({
  num,
  suffix,
  labelZh,
  labelEn,
  index
}: {
  num: number;
  suffix: string;
  labelZh: string;
  labelEn: string;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isVisible) {
      let startTimestamp: number | null = null;
      const duration = 2000;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * num));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          setCount(num);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isVisible, num]);
  return <div ref={ref} data-index={index} className="flex flex-col items-center justify-center text-center p-8 md:p-12 transition-all duration-1000" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
  }}>
      <div className="text-6xl md:text-7xl lg:text-8xl font-mono font-bold text-primary mb-4 tracking-tighter drop-shadow-[0_0_15px_rgba(163,230,53,0.5)]">
        {count}
        <span className="text-primary">{suffix}</span>
      </div>
      
      {}
      <div className="w-full h-1 bg-white/10 mb-6 overflow-hidden rounded-md">
        <div className="h-full bg-primary transition-all duration-[2000ms] ease-out" style={{
        width: isVisible ? '100%' : '0%'
      }} />
      </div>

      <div className="text-foreground font-bold text-xl md:text-2xl mb-2 font-default">
        {labelZh}
      </div>
      <div className="text-muted-foreground text-sm md:text-base uppercase tracking-widest font-mono">
        {labelEn}
      </div>
    </div>;
}
export default function TheApexAdvantage() {
  return <section data-section-id="265225" id="the-apex-advantage" className="relative bg-[#14141a] py-16 md:py-24 overflow-hidden border-y border-white/5">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: `linear-gradient(0deg, transparent 0%, rgba(163,230,53,0.2) 50%, transparent 100%)`,
      backgroundSize: `100% 4px`,
      animation: 'scan 4s linear infinite'
    }} />
      
      <style jsx>{`
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
      
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {statsData.map((stat, i) => <AnimatedStat key={i} index={i} {...stat} />)}
        </div>
      </div>
    </section>;
}